// Onglet « Diagnostics » de la fiche logement (F-089) : registre, règles DPE versionnées, historique et pièces.
import { useState, type FormEvent } from "react";
import { del, post, put, upload } from "@core/api";
import { date } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Button, Card, ErrorBox, FileDrop, Input, Loading, Modal, Notice, Select, Table, Textarea, fieldError, useToast } from "@core/ui";
import { CLASS_OPTIONS, DiagChip, SourceLine, SourceLinks, TRI_OPTIONS, intOrNull, str, type DiagStatus, type RegistryItem, type Source } from "./shared";

type Diag = {
  id: string;
  kind: string;
  kind_label: string;
  label: string;
  performed_on: string;
  manual_valid_until: string | null;
  result: string;
  dpe_class: string | null;
  ges_class: string | null;
  energy_kwh_m2: number | null;
  ges_kg_m2: number | null;
  reference: string;
  diagnostician: string;
  notes: string;
  document_id: string | null;
  document_filename: string | null;
  document_url: string | null;
  replaced_by: string | null;
  valid_until: string | null;
  status: DiagStatus;
  status_label: string;
  validity: { rule: string; explanation: string };
};

type DpeRule = { status: string; class: string | null; from: string | null; message: string; rent_freeze: string | null; notes: string[]; sources: Source[]; rules_version: string };

type DiagResponse = {
  applies_to_housing: boolean;
  registry: { items: RegistryItem[]; missing: number; expired: number; soon: number; to_check: number; rules_version: string } | null;
  items: Diag[];
  facts: { construction_year: number | null; electricity_year: number | null; gas: string; gas_year: number | null; risk_zone: string; noise_zone: string; surface_known: boolean };
  dpe_rule: DpeRule;
  rules_version: string;
  general_source: Source;
  ademe: Source;
  georisques: Source;
  notice: string;
};

const KINDS = [
  { value: "dpe", label: "Diagnostic de performance énergétique (DPE)" },
  { value: "erp", label: "État des risques" },
  { value: "electricite", label: "Électricité" },
  { value: "gaz", label: "Gaz" },
  { value: "plomb", label: "Plomb (CREP)" },
  { value: "amiante", label: "Amiante" },
  { value: "bruit", label: "Bruit (aéroports)" },
  { value: "surface", label: "Surface habitable" },
  { value: "termites", label: "Termites" },
  { value: "autre", label: "Autre diagnostic" },
];

function resultOptions(kind: string) {
  if (kind === "plomb" || kind === "amiante" || kind === "termites")
    return [
      { value: "non_renseigne", label: "Non renseigné" },
      { value: "absence", label: kind === "plomb" ? "Absence ou sous le seuil réglementaire" : "Absence" },
      { value: "presence", label: kind === "plomb" ? "Présence au-delà du seuil" : "Présence" },
    ];
  if (kind === "electricite" || kind === "gaz")
    return [
      { value: "non_renseigne", label: "Non renseigné" },
      { value: "sans_anomalie", label: "Sans anomalie" },
      { value: "anomalies", label: "Anomalies relevées" },
    ];
  return [{ value: "non_renseigne", label: "Sans objet" }];
}

const APPLICABILITY: Record<string, string> = { requis: "Exigé", a_disposition: "À tenir à disposition", non_requis: "Non exigé", a_verifier: "À vérifier" };

const RULE_CLASS: Record<string, string> = { interdit: "pat-rule-interdit", interdit_a_venir: "pat-rule-a-venir", autorise: "pat-rule-autorise" };

export function DiagnosticsTab({ propertyId, canEdit }: { propertyId: string; canEdit: boolean }) {
  const org = useOrg();
  const url = org.api(`/properties/${propertyId}/diagnostics`);
  const q = useGet<DiagResponse>(url);
  const [editing, setEditing] = useState<{ diag?: Diag; kind?: string } | null>(null);
  const [attaching, setAttaching] = useState<Diag | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const d = q.data;
  const reg = d.registry;
  return (
    <div className="stack">
      <Notice title={`Règles appliquées au ${date(d.rules_version)}`}>
        {d.notice} <SourceLine source={d.general_source} />
      </Notice>
      <Card title="Classe énergétique et location" className={`pat-rule ${RULE_CLASS[d.dpe_rule.status] ?? ""}`}>
        <p>
          <strong>{d.dpe_rule.class ? `Classe ${d.dpe_rule.class}` : "Classe non renseignée"}</strong> — {d.dpe_rule.message}
        </p>
        {d.dpe_rule.rent_freeze ? <p>{d.dpe_rule.rent_freeze}</p> : null}
        <ul className="pat-list small muted">
          {d.dpe_rule.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
          <li>Outre-mer : calendrier différent, non appliqué par Bailivo.</li>
        </ul>
        <SourceLinks sources={d.dpe_rule.sources} />
      </Card>
      {reg ? (
        <Card
          title="Registre des diagnostics"
          actions={
            canEdit ? (
              <Button small onClick={() => setEditing({ kind: "dpe" })}>
                Ajouter un diagnostic
              </Button>
            ) : null
          }
        >
          <p className="small muted">
            {reg.missing} manquant{reg.missing > 1 ? "s" : ""} · {reg.expired} échu{reg.expired > 1 ? "s" : ""} · {reg.soon} échéance{reg.soon > 1 ? "s" : ""} proche{reg.soon > 1 ? "s" : ""} (moins de 90 jours) · {reg.to_check} à vérifier
          </p>
          <Table caption="Registre des diagnostics">
            <thead>
              <tr>
                <th>Diagnostic</th>
                <th>Exigence</th>
                <th>Statut</th>
                <th>Validité</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reg.items.map((i) => {
                const diag = d.items.find((x) => x.id === i.diagnostic_id);
                return (
                  <tr key={`${i.kind}-${i.diagnostic_id ?? "aucun"}`}>
                    <td>
                      {i.kind_label}
                      <span className="pat-sub">
                        <a href={i.source.url} target="_blank" rel="noreferrer">
                          Source
                        </a>
                      </span>
                    </td>
                    <td className="small">
                      <strong>{APPLICABILITY[i.applicability.code]}</strong>
                      <span className="pat-sub">{i.applicability.reason}</span>
                    </td>
                    <td>
                      <DiagChip status={i.status} />
                      <span className="pat-sub">{i.message}</span>
                    </td>
                    <td className="small">
                      {i.performed_on ? `Réalisé le ${date(i.performed_on)}` : "—"}
                      {i.valid_until ? <span className="pat-sub">Jusqu’au {date(i.valid_until)}</span> : null}
                      {i.diagnostic_id ? <span className="pat-sub">{i.has_document ? "Pièce jointe" : "Pièce non jointe"}</span> : null}
                    </td>
                    <td className="right">
                      {canEdit ? (
                        <div className="pat-actions">
                          {diag ? (
                            <>
                              <Button small variant="discret" onClick={() => setAttaching(diag)}>
                                {i.has_document ? "Remplacer la pièce" : "Joindre la pièce"}
                              </Button>
                              <Button small variant="discret" onClick={() => setEditing({ kind: i.kind })}>
                                Nouveau
                              </Button>
                            </>
                          ) : i.status !== "non_requis" ? (
                            <Button small variant="secondaire" onClick={() => setEditing({ kind: i.kind })}>
                              Ajouter
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <p className="small muted">
            Une pièce déposée n’en prouve ni l’authenticité ni la conformité. Le numéro d’un DPE peut être recherché sur{" "}
            <a href={d.ademe.url} target="_blank" rel="noreferrer">
              l’observatoire de l’ADEME
            </a>{" "}
            : Bailivo ne l’interroge pas automatiquement.
          </p>
        </Card>
      ) : (
        <Notice>Le registre porte sur les logements ; ce bien n’a pas de registre propre.</Notice>
      )}
      <FactsCard propertyId={propertyId} facts={d.facts} canEdit={canEdit} georisques={d.georisques} />
      <Card title="Historique">
        {d.items.length === 0 ? (
          <p className="muted">Aucun diagnostic enregistré.</p>
        ) : (
          <Table caption="Historique des diagnostics">
            <thead>
              <tr>
                <th>Diagnostic</th>
                <th>Réalisé le</th>
                <th>Résultat</th>
                <th>Validité</th>
                <th>Pièce</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.items.map((x) => (
                <tr key={x.id}>
                  <td>
                    {x.kind_label}
                    {x.replaced_by ? <span className="pat-sub">Remplacé par un diagnostic plus récent</span> : null}
                    {x.reference ? <span className="pat-sub mono">{x.reference}</span> : null}
                  </td>
                  <td>{date(x.performed_on)}</td>
                  <td className="small">
                    {x.dpe_class ? `DPE ${x.dpe_class}` : ""}
                    {x.ges_class ? ` · GES ${x.ges_class}` : ""}
                    {resultOptions(x.kind).find((r) => r.value === x.result && r.value !== "non_renseigne")?.label ?? ""}
                  </td>
                  <td className="small">
                    <DiagChip status={x.status} />
                    <span className="pat-sub">{x.validity.explanation}</span>
                  </td>
                  <td className="small">
                    {x.document_url ? (
                      <a href={x.document_url} target="_blank" rel="noreferrer">
                        {x.document_filename ?? "Ouvrir"}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="right">
                    {canEdit ? (
                      <div className="pat-actions">
                        <Button small variant="discret" onClick={() => setEditing({ diag: x, kind: x.kind })}>
                          Corriger
                        </Button>
                        <RemoveDiag propertyId={propertyId} diag={x} />
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.diag ? "Corriger un diagnostic" : "Ajouter un diagnostic"}>
        {editing ? <DiagForm propertyId={propertyId} diag={editing.diag} kind={editing.kind} onDone={() => setEditing(null)} /> : null}
      </Modal>
      <Modal open={!!attaching} onClose={() => setAttaching(null)} title="Joindre la pièce du diagnostic">
        {attaching ? <AttachDoc propertyId={propertyId} diag={attaching} onDone={() => setAttaching(null)} /> : null}
      </Modal>
    </div>
  );
}

function RemoveDiag({ propertyId, diag }: { propertyId: string; diag: Diag }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [confirm, setConfirm] = useState(false);
  return (
    <>
      <Button small variant="discret" onClick={() => setConfirm(true)}>
        Retirer
      </Button>
      <Modal
        open={confirm}
        onClose={() => setConfirm(false)}
        title="Retirer ce diagnostic"
        footer={
          <>
            <Button variant="discret" onClick={() => setConfirm(false)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                try {
                  await del(org.api(`/properties/${propertyId}/diagnostics/${diag.id}`));
                  invalidate(org.api(`/properties`), org.api("/patrimoine"));
                  toast("Diagnostic retiré.");
                  setConfirm(false);
                } catch (e: any) {
                  toast(e.message, "erreur");
                }
              }}
            >
              Retirer
            </Button>
          </>
        }
      >
        <p>
          À utiliser pour une saisie erronée. Le diagnostic « {diag.kind_label} » du {date(diag.performed_on)} disparaît du registre ; la trace reste au journal et le diagnostic précédent redevient en vigueur.
        </p>
      </Modal>
    </>
  );
}

function DiagForm({ propertyId, diag, kind, onDone }: { propertyId: string; diag?: Diag; kind?: string; onDone: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [f, setF] = useState({
    kind: diag?.kind ?? kind ?? "dpe",
    label: diag?.label ?? "",
    performed_on: diag?.performed_on ?? "",
    manual_valid_until: diag?.manual_valid_until ?? "",
    result: diag?.result ?? "non_renseigne",
    dpe_class: diag?.dpe_class ?? "",
    ges_class: diag?.ges_class ?? "",
    energy_kwh_m2: str(diag?.energy_kwh_m2),
    ges_kg_m2: str(diag?.ges_kg_m2),
    reference: diag?.reference ?? "",
    diagnostician: diag?.diagnostician ?? "",
    notes: diag?.notes ?? "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));
  const err = (k: string) => fieldError(error, k);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      ...f,
      manual_valid_until: f.kind === "autre" ? f.manual_valid_until || null : null,
      dpe_class: f.kind === "dpe" ? f.dpe_class || null : null,
      ges_class: f.kind === "dpe" ? f.ges_class || null : null,
      energy_kwh_m2: f.kind === "dpe" ? intOrNull(f.energy_kwh_m2) : null,
      ges_kg_m2: f.kind === "dpe" ? intOrNull(f.ges_kg_m2) : null,
    };
    try {
      const base = org.api(`/properties/${propertyId}/diagnostics`);
      const saved = diag ? await put<{ id: string }>(`${base}/${diag.id}`, body) : await post<{ id: string }>(base, body);
      if (file) {
        const form = new FormData();
        form.append("file", file);
        await upload(`${base}/${saved.id}/document`, form);
      }
      invalidate(org.api("/properties"), org.api("/patrimoine"));
      toast(diag ? "Diagnostic corrigé." : "Diagnostic ajouté.");
      onDone();
    } catch (e2) {
      setError(e2);
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="stack" onSubmit={submit} noValidate>
      <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
      <Select label="Type" value={f.kind} options={KINDS} disabled={!!diag} error={err("kind")} onChange={(e) => set("kind", e.target.value)} />
      {f.kind === "autre" ? <Input label="Nom du diagnostic" required value={f.label} error={err("label")} onChange={(e) => set("label", e.target.value)} /> : null}
      <div className="form-row">
        <Input label="Date de réalisation" type="date" required value={f.performed_on} error={err("performed_on")} onChange={(e) => set("performed_on", e.target.value)} />
        {f.kind === "autre" ? (
          <Input label="Fin de validité" type="date" value={f.manual_valid_until} error={err("manual_valid_until")} onChange={(e) => set("manual_valid_until", e.target.value)} />
        ) : (
          <p className="small muted">La fin de validité est calculée selon les règles appliquées par Bailivo.</p>
        )}
      </div>
      {f.kind === "dpe" ? (
        <div className="form-row">
          <Select label="Classe énergie" value={f.dpe_class} options={CLASS_OPTIONS} error={err("dpe_class")} onChange={(e) => set("dpe_class", e.target.value)} />
          <Select label="Classe climat (GES)" value={f.ges_class} options={CLASS_OPTIONS} error={err("ges_class")} onChange={(e) => set("ges_class", e.target.value)} />
          <Input label="Consommation (kWh/m²/an)" inputMode="numeric" value={f.energy_kwh_m2} error={err("energy_kwh_m2")} onChange={(e) => set("energy_kwh_m2", e.target.value)} />
          <Input label="Émissions (kg CO₂/m²/an)" inputMode="numeric" value={f.ges_kg_m2} error={err("ges_kg_m2")} onChange={(e) => set("ges_kg_m2", e.target.value)} />
        </div>
      ) : resultOptions(f.kind).length > 1 ? (
        <Select label="Résultat" value={f.result} options={resultOptions(f.kind)} error={err("result")} onChange={(e) => set("result", e.target.value)} />
      ) : null}
      <div className="form-row">
        <Input label={f.kind === "dpe" ? "Numéro du DPE (ADEME)" : "Référence du rapport"} value={f.reference} error={err("reference")} onChange={(e) => set("reference", e.target.value)} />
        <Input label="Diagnostiqueur" value={f.diagnostician} onChange={(e) => set("diagnostician", e.target.value)} />
      </div>
      <Textarea label="Notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
      <div className="stack-sm">
        <FileDrop label={file ? "Changer de fichier" : "Joindre le rapport"} onFiles={(fs) => setFile(fs[0] ?? null)} />
        {file ? <p className="small">Fichier choisi : {file.name}</p> : null}
      </div>
      <div className="form-actions">
        <Button variant="discret" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" busy={busy}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

function AttachDoc({ propertyId, diag, onDone }: { propertyId: string; diag: Diag; onDone: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="stack">
      <p>
        {diag.kind_label} du {date(diag.performed_on)}. La pièce est stockée chiffrée ; une nouvelle version remplace la précédente, qui reste tracée.
      </p>
      <ErrorBox error={error} />
      <FileDrop
        busy={busy}
        onFiles={async (files) => {
          if (!files[0]) return;
          setBusy(true);
          setError(null);
          try {
            const form = new FormData();
            form.append("file", files[0]);
            await upload(org.api(`/properties/${propertyId}/diagnostics/${diag.id}/document`), form);
            invalidate(org.api("/properties"));
            toast("Pièce jointe.");
            onDone();
          } catch (e) {
            setError(e);
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}

function FactsCard({ propertyId, facts, canEdit, georisques }: { propertyId: string; facts: DiagResponse["facts"]; canEdit: boolean; georisques: Source }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [f, setF] = useState({
    construction_year: str(facts.construction_year),
    electricity_year: str(facts.electricity_year),
    gas: facts.gas ?? "inconnu",
    gas_year: str(facts.gas_year),
    risk_zone: facts.risk_zone ?? "inconnu",
    noise_zone: facts.noise_zone ?? "inconnu",
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));
  return (
    <Card title="Informations qui déterminent les diagnostics exigés">
      <p className="small muted">
        Bailivo ne devine pas ces informations. « Je ne sais pas » reste affiché « à vérifier » dans le registre. Pour les risques d’une adresse, consultez{" "}
        <a href={georisques.url} target="_blank" rel="noreferrer">
          Géorisques
        </a>
        .
      </p>
      <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
      <div className="form-row">
        <Input label="Année de construction" inputMode="numeric" disabled={!canEdit} value={f.construction_year} error={fieldError(error, "details.construction_year")} onChange={(e) => set("construction_year", e.target.value)} />
        <Input label="Année de l’installation électrique" inputMode="numeric" disabled={!canEdit} value={f.electricity_year} error={fieldError(error, "details.electricity_year")} onChange={(e) => set("electricity_year", e.target.value)} />
        <Select label="Installation de gaz" disabled={!canEdit} value={f.gas} options={TRI_OPTIONS} onChange={(e) => set("gas", e.target.value)} />
        {f.gas === "oui" ? <Input label="Année de l’installation de gaz" inputMode="numeric" disabled={!canEdit} value={f.gas_year} error={fieldError(error, "details.gas_year")} onChange={(e) => set("gas_year", e.target.value)} /> : null}
        <Select label="Zone ou périmètre à risques" disabled={!canEdit} value={f.risk_zone} options={TRI_OPTIONS} onChange={(e) => set("risk_zone", e.target.value)} />
        <Select label="Zone de bruit d’un aéroport" disabled={!canEdit} value={f.noise_zone} options={TRI_OPTIONS} onChange={(e) => set("noise_zone", e.target.value)} />
      </div>
      {canEdit ? (
        <div className="form-actions">
          <Button
            variant="secondaire"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await put(org.api(`/properties/${propertyId}/facts`), {
                  construction_year: intOrNull(f.construction_year),
                  electricity_year: intOrNull(f.electricity_year),
                  gas: f.gas,
                  gas_year: f.gas === "oui" ? intOrNull(f.gas_year) : null,
                  risk_zone: f.risk_zone,
                  noise_zone: f.noise_zone,
                });
                invalidate(org.api("/properties"), org.api("/patrimoine"));
                toast("Informations enregistrées.");
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            Enregistrer ces informations
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
